import {
	addDays,
	endOfDay,
	format,
	isBefore,
	isSameDay,
	isSameMonth,
	isSameYear,
	set,
	type FormatDateOptions as DateFnsFormatDateOptions,
	type Locale,
} from 'date-fns';
import { cs, de, enGB, sk } from 'date-fns/locale';

import { currencyEnumBase, type CURRENCY } from '../constants/currency';
import { isDefined } from './guards';

/**
 * Internalized number formatter util
 * @param locale A locale code
 * @param settings Number format options from Intl
 * @returns Intl Number format util
 */
export const numberFormatUtil = (locale: string, settings?: Intl.NumberFormatOptions) =>
	Intl.NumberFormat(locale, {
		minimumFractionDigits: 0,
		maximumFractionDigits: 2,
		...settings,
	});

/**
 * Internalized number formatter
 * @param value A numeric value to format
 * @param locale A locale code
 * @param settings Number format options from Intl
 * @returns Formatted number output
 */
export const formatNumber = (value: number, locale: string, settings?: Intl.NumberFormatOptions) => numberFormatUtil(locale, settings).format(value);

/**
 * Internalized price formatter
 * @param currencyId Enumerated Currency ID to foramt the price in
 * @param locale A locale code
 * @param settings Number format options from Intl
 * @returns Formatted price output
 */
export const formatPrice = (currencyId: CURRENCY, locale: string, settings?: Intl.NumberFormatOptions) => {
	const currency = currencyEnumBase.get(currencyId);
	return numberFormatUtil(locale, {
		...settings,
		style: 'currency',
		currency: currency.isoChar,
	});
};

/**
 * Formats date in the specified format
 * @param date Date to format
 * @param dateFormat Tokenized date format
 * @param options Formatting options
 * @returns Formatted date
 */
export const formatDate = (date: Date, dateFormat: string, options?: FormatDateOptions): string => format(date, dateFormat, options);

/**
 * Returns date-fns locale based on language code
 * @param language Language code
 * @returns DateFnsLocale
 */
export const getDateFnsLocale = (language: string): Locale => {
	const dateLocales = { de, sk, en: enGB, cs } as const;
	return language in dateLocales ? dateLocales[language] : cs;
};

/**
 * Returns formatted date range as string.
 * Optionally, you can pass `eventLike` option to format the range as an event-like date, meaning that *end of day* is shfited by 8 hours.
 * @param startDate Start date of the range
 * @param endDate End date of the range, optional
 * @param formatDateOptions Formatting options
 * @returns Formatted date range
 */
export const formatDateRange = (startDate: Date, endDate?: Date, formatDateOptions?: FormatDateRangeOptions): string => {
	/** 0. startDate > endDate -> error */
	if (isDefined(endDate) && isBefore(endDate, startDate)) throw new Error('[formatDateRange]: `endDate` cannot be before `startDate`!');

	/** define correct end date, since "end" date of event-like dates is a bit longer rather than midnight */
	const _startDate = startDate;
	const _endDate =
		formatDateOptions?.eventLike && isDefined(endDate) && isBefore(endDate, addDays(set(startDate, { hours: 8 }), 1)) ? endOfDay(startDate) : endDate;

	/** 1.1 no `_endDate` -> format all in _startDate */
	/** 1.2. same day -> "1. června 2022" */
	if (!isDefined(_endDate) || isSameDay(_startDate, _endDate)) {
		return formatDate(_startDate, 'PPPP', formatDateOptions);
	}

	/** 2. same month, diff day -> "1. - 3. června 2022" */
	if (isSameMonth(_startDate, _endDate)) {
		return `${formatDate(_startDate, 'do', formatDateOptions)} - ${formatDate(_endDate, 'do MMMM yyyy', formatDateOptions)}`;
	}

	/** 3. same year, diff month, diff day -> "1. Bře - 1. Dub 2022" */
	if (isSameYear(_startDate, _endDate)) {
		return `${formatDate(_startDate, 'do MMM', formatDateOptions)} - ${formatDate(_endDate, 'do MMM yyyy', formatDateOptions)}`;
	}

	/** 4. diff year, diff month, diff day -> "1. 3. 2022 - 1. 4. 2023" */
	return `${formatDate(_startDate, 'do M. yyyy', formatDateOptions)} - ${formatDate(_endDate, 'do M. yyyy', formatDateOptions)}`;
};

/**
 * FormatDateRangeOptions type
 * @extends {DateFnsFormatDateOptions}
 */
export interface FormatDateRangeOptions extends DateFnsFormatDateOptions {
	eventLike?: boolean;
}

/**
 * FormatDateOptions type
 * @extends {DateFnsFormatDateOptions}
 */
export interface FormatDateOptions extends DateFnsFormatDateOptions {}
